import {
    ApiContentSection,
    ApiSectionType,
    buildHtmlSectionShadowStyles,
    hasInlineHandlers,
    INSPECT_ATTR,
} from '@storefronts/common-types'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { ShadowDomWrapper } from './ShadowDomWrapper'
import { SectionContainer, SectionTitle } from './Shared'

// Run before paint on the client so a client-side navigation that reuses this
// host updates the shadow root before the old block flashes; fall back to
// useEffect on the server (useLayoutEffect warns there).
const useShadowEffect =
    typeof document !== 'undefined' ? useLayoutEffect : useEffect

const SHOPPER_HTML_SECTION_SHADOW_STYLES = buildHtmlSectionShadowStyles(true)

type HTMLSection = Extract<ApiContentSection, { type: ApiSectionType.HTML }>

type HTMLSectionProps = {
    section: HTMLSection
}

// Escape </template> so a stray closing tag in author HTML can't end the
// shadow-DOM wrapper early.
const escapeClosingTemplate = (html: string): string =>
    html.replace(/<\/(template)/gi, '&lt;/$1')

const SsrHtmlSection: React.FC<{ section: HTMLSection; html: string }> = ({
    section,
    html,
}) => {
    const hostRef = useRef<HTMLDivElement>(null)
    const renderedHtmlRef = useRef<string | null>(null)

    const ssrInnerHTML = `<template shadowrootmode="open"><style>${SHOPPER_HTML_SECTION_SHADOW_STYLES}</style><div ${INSPECT_ATTR.HTML_ROOT}>${escapeClosingTemplate(
        html,
    )}</div></template>`

    useShadowEffect(() => {
        const host = hostRef.current
        if (!host) return

        // First load: the parser already attached the DSD root — leave it as-is.
        if (renderedHtmlRef.current === null && host.shadowRoot) {
            renderedHtmlRef.current = html
            return
        }
        if (renderedHtmlRef.current === html) return

        // No DSD support, or content changed on client nav (hosts are reused
        // across pages, keyed by index): (re)render into the shadow root.
        host.querySelector(':scope > template[shadowrootmode]')?.remove()
        const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' })
        root.innerHTML = `<style>${SHOPPER_HTML_SECTION_SHADOW_STYLES}</style><div ${INSPECT_ATTR.HTML_ROOT}>${escapeClosingTemplate(
            html,
        )}</div>`
        renderedHtmlRef.current = html
    }, [html])

    return (
        <SectionContainer verticalPadding={0}>
            {section.sectionTitle && (
                <SectionTitle isHorizontalScroll>
                    {section.sectionTitle}
                </SectionTitle>
            )}
            <div
                ref={hostRef}
                suppressHydrationWarning
                dangerouslySetInnerHTML={{ __html: ssrInnerHTML }}
            />
        </SectionContainer>
    )
}

const ClientHtmlSection: React.FC<{ section: HTMLSection; html: string }> = ({
    section,
    html,
}) => (
    <SectionContainer verticalPadding={0}>
        {section.sectionTitle && (
            <SectionTitle isHorizontalScroll>
                {section.sectionTitle}
            </SectionTitle>
        )}
        <ShadowDomWrapper styles={SHOPPER_HTML_SECTION_SHADOW_STYLES}>
            <div
                {...{ [INSPECT_ATTR.HTML_ROOT]: '' }}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </ShadowDomWrapper>
    </SectionContainer>
)

export const HTMLSection: React.FC<HTMLSectionProps> = ({ section }) => {
    let html = section.html
    try {
        html = decodeURIComponent(html)
    } catch (_e) {}

    return hasInlineHandlers(html) ? (
        <ClientHtmlSection section={section} html={html} />
    ) : (
        <SsrHtmlSection section={section} html={html} />
    )
}
